import type { SignedOutReason } from '@/api/auth-events';

/**
 * Why the person was signed out, kept for the sign-in page to say once
 * (portal-session-rules, B15, I6).
 *
 * In this tab's sessionStorage, because the sign-in page is a fresh render
 * after the session ends and nothing else survives that. The person's id is
 * kept with it so the sign-in page can tell whether whoever signs in next is
 * the same person (I7, B18).
 */

const KEY = 'activistarc.portal.signed-out';

/** The sentences of D11, D15 and D16. A Log Out the person chose has none. */
const SENTENCES: Record<Exclude<SignedOutReason, 'manual'>, string> = {
  'signed-in-elsewhere':
    'You were signed out because your account signed in on another device or browser.',
  'password-changed': 'Your password was changed. Sign in with your new password.',
  revoked: 'Your session has ended. Please sign in again.',
  expired: 'Your session has ended. Please sign in again.',
  inactive:
    "Your portal account is no longer active. Contact your organization's Super Admin.",
  'no-organization': 'You no longer belong to any organization.',
};

export interface SignOutNotice {
  /** What to tell the person; null for a Log Out they chose, or once it has been said. */
  sentence: string | null;
  /** Whose session it was, kept until somebody signs in again in this tab (I7). */
  userId: string | null;
}

function isNotice(value: unknown): value is SignOutNotice {
  if (!value || typeof value !== 'object') return false;
  const notice = value as Record<string, unknown>;
  return (
    (notice.sentence === null || typeof notice.sentence === 'string') &&
    (notice.userId === null || typeof notice.userId === 'string')
  );
}

function store(notice: SignOutNotice): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(notice));
  } catch {
    // Private windows and blocked site data both throw. The person is still
    // signed out; they are only not told why.
  }
}

export const signOutNotice = {
  /**
   * Records why a session ended and whose it was. A Log Out the person chose
   * has nothing to explain, so no sentence - and no older one is left in its
   * place - but whose it was is kept all the same (I6, I7).
   */
  write(reason: SignedOutReason, userId: string | null): void {
    store({ sentence: reason === 'manual' ? null : SENTENCES[reason], userId });
  },

  /** The sentence has been said; whose session it was is still needed (I6, I7). */
  clearSentence(): void {
    const notice = this.read();
    if (notice) store({ ...notice, sentence: null });
  },

  /**
   * The notice, without clearing it. Reading twice is harmless, which matters:
   * React's strict mode draws a page's first state twice.
   */
  read(): SignOutNotice | null {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      return isNotice(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },

  /** Clears the notice: somebody has signed in again in this tab. */
  clear(): void {
    try {
      sessionStorage.removeItem(KEY);
    } catch {
      // Nothing to do.
    }
  },
};
